import { Currency, SubscriptionsRepository } from '@packlink/packlink-sdk';
import { apiClient } from '@sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SUBSCRIPTION_RETENTION_DISCOUNT_CACHE_KEY, SUBSCRIPTIONS_CACHE_KEY } from '../constants';
import { useTenantConfig } from '@packlink/tenant-config-provider';
import { ITenantConfig } from '@types';
import { useDateFormat } from '@hooks';
import { useTranslation } from '@packlink/translation-provider';
import { useToast } from '@shipengine/giger';
import { useLastClientSubscription } from '@Subscriptions/hooks/useClientSubscriptions';

const subscriptionsRepository = new SubscriptionsRepository(apiClient);

export function useRetentionDiscount(onClose?: () => void) {
    const { t } = useTranslation();
    const toast = useToast();
    const queryClient = useQueryClient();
    const { defaultCurrency } = useTenantConfig<ITenantConfig>();
    const { isActive } = useLastClientSubscription();

    const { data: retentionDiscount, isLoading } = useQuery({
        queryKey: [SUBSCRIPTION_RETENTION_DISCOUNT_CACHE_KEY, 'eligibility'],
        queryFn: () => subscriptionsRepository.getRetentionDiscountEligibility(),
        enabled: isActive,
    });

    const {
        data,
        mutate: applyRetentionDiscount,
        isPending,
        isSuccess,
    } = useMutation({
        mutationKey: [SUBSCRIPTION_RETENTION_DISCOUNT_CACHE_KEY, 'apply'],
        mutationFn: () => subscriptionsRepository.applyRetentionDiscount(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_CACHE_KEY] });
        },
        onError: () => {
            toast.error({ message: t('INTERNAL_SERVER_ERROR') });
            onClose?.();
        },
    });

    const currency =
        retentionDiscount?.eligible && retentionDiscount?.discount?.type === 'fixed'
            ? retentionDiscount?.discount?.currency
            : defaultCurrency;

    const discountValue =
        retentionDiscount?.eligible && retentionDiscount?.discount?.type === 'percentage'
            ? `${retentionDiscount?.discount.value}%`
            : (Currency.format(retentionDiscount?.discount?.value ?? 0, currency) ?? undefined);

    return {
        isEligibleForDiscount: retentionDiscount?.eligible,
        discountValue,
        discountedPrice: Number(retentionDiscount?.discountedAmount),
        fullPrice: Number(retentionDiscount?.currentAmount),
        applyRetentionDiscount,
        loadingEligibility: isLoading,
        pendingApplication: isPending,
        discountApplied: isSuccess,
        startDate: useDateFormat(data?.currentPeriodEndsAt),
        discountCurrency: currency,
    };
}
